export type AgentOrbState = "idle" | "thinking" | "listening" | "talking"

/** The accent, a light tone of it, and a neighbouring hue for the shaded flank. */
export type AgentOrbColors = readonly [string, string, string]

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

/** Input and output level (0 to 1) each state drives the orb with. Curves follow elevenlabs/ui's Orb (MIT). */
export function orbTargetLevels(
	state: AgentOrbState,
	t: number,
): { input: number; output: number } {
	switch (state) {
		case "listening":
			return { input: clamp01(0.55 + Math.sin(t * 3.2) * 0.35), output: 0.45 }
		case "talking":
			return {
				input: clamp01(0.65 + Math.sin(t * 4.8) * 0.22),
				output: clamp01(0.75 + Math.sin(t * 3.6) * 0.22),
			}
		case "thinking": {
			const base = 0.38 + 0.07 * Math.sin(t * 0.7)
			const wander = 0.05 * Math.sin(t * 2.1) * Math.sin(t * 0.37 + 1.2)
			return {
				input: clamp01(base + wander),
				output: clamp01(0.48 + 0.12 * Math.sin(t * 1.05 + 0.6)),
			}
		}
		default:
			return { input: 0, output: 0.3 }
	}
}

export function parseHexColor(hex: string): [number, number, number] | null {
	const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
	if (!m) return null
	const h =
		m[1]!.length === 3
			? m[1]!
					.split("")
					.map((c) => c + c)
					.join("")
			: m[1]!
	return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [
		number,
		number,
		number,
	]
}

function rgbToHsl([r, g, b]: [number, number, number]) {
	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const l = (max + min) / 2
	if (max === min) return { h: 0, s: 0, l }
	const d = max - min
	const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
	const h =
		max === r
			? (g - b) / d + (g < b ? 6 : 0)
			: max === g
				? (b - r) / d + 2
				: (r - g) / d + 4
	return { h: h * 60, s, l }
}

function hslToHex(h: number, s: number, l: number) {
	const a = s * Math.min(l, 1 - l)
	const f = (n: number) => {
		const k = (n + h / 30) % 12
		const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
		return Math.round(c * 255)
			.toString(16)
			.padStart(2, "0")
	}
	return `#${f(0)}${f(8)}${f(4)}`
}

const paletteFromHue = (
	h: number,
	s: number,
	accent?: string,
): AgentOrbColors => {
	const sat = Math.min(0.9, Math.max(0.55, s))
	return [
		accent ?? hslToHex(h, sat, 0.55),
		hslToHex((h + 15) % 360, sat * 0.85, 0.86),
		hslToHex((h + 320) % 360, sat * 0.8, 0.64),
	]
}

// Sky blue, the orb a portal gets when its accent is grey, black or white.
export const DEFAULT_AGENT_ORB_COLORS: AgentOrbColors = paletteFromHue(212, 0.8)

/** The accent, a light tone of it, and a neighbouring hue for the shaded flank; greys fall back to the default orb. */
export function agentOrbColorsFromAccent(
	accent: string | null | undefined,
): AgentOrbColors {
	const rgb = accent ? parseHexColor(accent) : null
	if (!rgb) return DEFAULT_AGENT_ORB_COLORS
	const { h, s, l } = rgbToHsl(rgb)
	if (s < 0.2 || l < 0.08 || l > 0.94) return DEFAULT_AGENT_ORB_COLORS
	// A very dark or very pale accent would flatten the ball, so those get a mid tone of the same hue.
	const usable = l > 0.3 && l < 0.75
	return paletteFromHue(h, s, usable ? hslToHex(h, s, l) : undefined)
}
